import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import _ from "lodash";

import { debounce } from "throttle-debounce";

import { useTranslation } from "react-i18next";

import { mapColumnsToGroupFields, mapMacroFiltersToMaterials } from "../../../utils/mappers/genericGroups";

import {
  Segment,
  Form,
  Grid,
  Loader,
  Dimmer,
  GridColumn,
  GridRow,
  Button,
} from "semantic-ui-react";

import MultipleDropdown from "./GenGroupMultipleDropdown";
import FiltersDropdown from "../../GroupSettings/FiltersDropdown/FiltersDropdown";
import SubgroupDropdown from "../../shared/MaterialCheckboxSelector";

import "./EditGroup.css";

const EditEventGroup = ({
  groupNames,
  selectedGroup,
  columns,
  items,
  closeEditEventGroup,
  onSave,
  loading,
}) => {
  const { t } = useTranslation();
  const {setCreatingNewGroup, setSelectedEventGroup} = closeEditEventGroup

  const groupFields = useMemo(() => mapColumnsToGroupFields(t, columns, items), [t, columns, items]);
  const granularMaterialInfo = useMemo(
    () => groupFields.find(field => field.accessor === 'location_code_concatenation'),
    [groupFields]
  );

  const def = {};
  for (let field of groupFields) {
    def[[field.accessor]] = {
      name: field.name,
      accessor: field.accessor,
      values: ["all"],
    };
  }
  const [duplicateNameError, setDuplicateNameError] = useState(null);
  const initialGroupData = _.isEmpty(selectedGroup)
    ? { ...def }
    : { ...selectedGroup };
  for (let field of groupFields) {
    if (_.isNil(initialGroupData[[field.accessor]])) {
      initialGroupData[[field.accessor]] = {
        name: field.name,
        accessor: field.accessor,
        values: ["all"],
      };
    }
  }
  const getPremarkedGranularMaterials = () => {
    if(_.isEmpty(selectedGroup)){
      return mapMacroFiltersToMaterials(initialGroupData, items)
    }
    if(selectedGroup.location_code_concatenation.values.includes('all')){
      return mapMacroFiltersToMaterials(selectedGroup, items)
    }
    return selectedGroup.location_code_concatenation.values
  }

  const [newGroupData, setNewGroupData] = useState(initialGroupData);
  const [equivalentMaterialIds, setEquivalentMaterialIds] = useState(getPremarkedGranularMaterials())

  const debouncedGroupData = debounce(500, (fieldData) => {
    updateNewGroupData({ ...newGroupData, ...fieldData });
  });

  const updateNewGroupData = (fieldData) => {
    if (Object.keys(fieldData).includes('name') && _.isNil(selectedGroup)){
      const equivalentMaterialIds = mapMacroFiltersToMaterials({ ...newGroupData, ...fieldData }, items)
      !_.isNil(equivalentMaterialIds) ? setEquivalentMaterialIds(equivalentMaterialIds): setEquivalentMaterialIds([])
      setNewGroupData({ ...newGroupData, ...fieldData });
      return

    }
    if (Object.keys(fieldData).includes('location_code_concatenation')){
      return

    }
    const equivalentMaterialIds = mapMacroFiltersToMaterials({ ...newGroupData, ...fieldData }, items)
    !_.isNil(equivalentMaterialIds) ? setEquivalentMaterialIds(equivalentMaterialIds): setEquivalentMaterialIds([])
    setNewGroupData({ ...newGroupData, ...fieldData });

  };

  const initialMarked = {};
  groupFields.forEach((field) => (initialMarked[field.accessor] = true));
  const [markedFields, setMarkedFields] = useState(initialMarked);

  const updateMarkedFields = (accessor, value) => {
    setMarkedFields({ ...markedFields, [accessor]: value });
  };

  const materialSelectionRef = useRef(equivalentMaterialIds);
  useEffect(() => {
    materialSelectionRef.current = equivalentMaterialIds;
  }, [equivalentMaterialIds]);

  const handleMaterialChange = useCallback((options) => {
    materialSelectionRef.current = _.isEmpty(options) ? ["all"] : options;
  }, []);

  return (
    <Segment basic className={"edit-group"}>
      <Dimmer.Dimmable dimmed={loading}>
        <Dimmer inverted active={loading}>
          {loading && (
            <div className="ui container">
              <Loader active size="large" />
            </div>
          )}
        </Dimmer>

        <Form
          className={"edit-group"}
          autoComplete="off"
          onSubmit={() => {
            const filtered = {
              ...newGroupData,
              ...(granularMaterialInfo && {
                location_code_concatenation: {
                  name: granularMaterialInfo.name,
                  accessor: "location_code_concatenation",
                  values: materialSelectionRef.current,
                },
              }),
            };
            // Cleanup before dispatch
            Object.keys(filtered).forEach(
              (key) => filtered[key] === undefined && delete filtered[key]
            );

            if (
              !_.isNil(selectedGroup)? newGroupData.name.trim() !== selectedGroup.name &&
              _.includes(groupNames, newGroupData.name.trim()) :
              _.includes(groupNames, newGroupData.name.trim())
            ) {
              setDuplicateNameError({
                content: t("groupSettings.duplicateGroup"),
                pointing: "above",
              });
              return;
            }
            filtered.group_type = 'event_group'
            // console.log('on save arg', filtered)
            setDuplicateNameError(null);
            onSave(filtered);
            setCreatingNewGroup(false)
            setSelectedEventGroup(null)
          }}
        >
          <Grid columns={2} className="edit-eventgroup-controls-container">
            {/* <GridRow> */}
            <GridColumn stretched  floated="left"  className="macrofilters-container">
              <Segment className="macrofilters-container">
                <Form.Group className="top-labels" widths={2}>
                  <Form.Input
                    className="group-name-input"
                    required
                    error={duplicateNameError}
                    placeholder="Add a name to this group"
                    name="name"
                    defaultValue={_.get(selectedGroup, "name", "")}
                    onChange={(e) => {
                      debouncedGroupData({ name: e.target.value.trim() });
                    }}
                    label={t("groupSettings.nameLabel")}
                  />
                  <Form.Field>
                    <label>{t("groupSettings.filters")}</label>
                    <FiltersDropdown
                      groupFields={groupFields}
                      markedGroupFields={groupFields
                        .filter(
                          (field) => markedFields[field.accessor] === true
                        )
                        .map((field) => field.accessor)}
                      onChange={updateMarkedFields}
                    ></FiltersDropdown>
                  </Form.Field>
                </Form.Group>

                <Grid stackable className="group-filters-container">
                  {groupFields
                    .filter((field) => 
                      markedFields[field.accessor] === true && field.accessor !== 'location_code_concatenation')
                    .map((field) => (
                      <Grid.Column
                        stretched
                        width={4}
                        key={field.accessor}
                        className={"group-field-dropdown"}
                      >
                        <label className="dropdown-group-fields">{field.name}</label>
                        <MultipleDropdown
                          options={field.options}
                          defaultValues={
                            _.get(selectedGroup, field.accessor, {}).values ||
                            _.get(newGroupData, field.accessor, {}).values
                          }
                          onChange={(options) =>
                            updateNewGroupData({
                              [field.accessor]: _.isEmpty(options)
                                ? {
                                  name: field.name,
                                  accessor: field.accessor,
                                  values: ["all"],
                                }
                                : {
                                  name: field.name,
                                  accessor: field.accessor,
                                  values: options,
                                },
                            })
                          }
                          groupType={""}
                        />
                      </Grid.Column>
                    ))}
                </Grid>
              </Segment>
              
            </GridColumn>
            <GridColumn >
              <GridRow className="granular-material-selector-container">
              <SubgroupDropdown
                options={ !_.isNil(granularMaterialInfo)  ? granularMaterialInfo.options : []}
                defaultValues={equivalentMaterialIds
                    }
                onChange={handleMaterialChange}
              />
              </GridRow>
              <GridRow >
                <Button.Group floated="right">
                  <Button
                    className="edit-event-group-buttons"
                        onClick={() => {
                          setCreatingNewGroup(false)
                          setSelectedEventGroup(null)
                        }}

                    >{t("cancel")}</Button>
                  <Button
                  className="edit-event-group-buttons-submit-button"
                  type='submit'

                >{t("save")}</Button>
                </Button.Group>
              </GridRow>
            </GridColumn>
          </Grid>
        </Form>
      </Dimmer.Dimmable>
    </Segment>
  );
};

EditEventGroup.propTypes = {
  selectedGroup: PropTypes.object,
  columns: PropTypes.object,
  items: PropTypes.array,
  closeEditEventGroup: PropTypes.func, 
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  groupNames: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  groups: PropTypes.array,
};

EditEventGroup.defaultProps = {
  selectedGroup: null,
  columns: [],
  items: [],
  groupNames: [],
  onSave: _.noop,
  onCancel: _.noop,
  loading: false,
  groups: [],
};

export default EditEventGroup;
